import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Clock, ExternalLink, Bot, LineChart, Coins, Loader2, Repeat, Compass, ArrowDownUp } from "lucide-react";
import SwapSTXOSHIButton from "@/components/SwapSTXOSHIButton";
import { NewsCard, timeAgo } from "@/components/NewsCard";
import { useSeo } from "@/hooks/useSeo";
import Header from "@/components/Header";
import { NewsShareBar } from "@/components/NewsShareBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const host = (u) => { try { const h = new URL(u).hostname.replace("www.", ""); return h === "news.google.com" ? "" : h; } catch { return ""; } };

export default function NewsArticle() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setData(null); setError(false); window.scrollTo(0, 0);
    axios.get(`${API}/news/article/${slug}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [slug]);

  const a = data?.article;
  const jsonLd = useMemo(() => a && ({
    "@context": "https://schema.org", "@type": "NewsArticle", headline: a.title,
    description: a.summary || a.title, datePublished: a.published_at, image: a.image ? [a.image] : undefined,
    mainEntityOfPage: `https://stxoshi.com/news/${a.slug}`, isBasedOn: a.url,
    author: { "@type": "Organization", name: a.source },
    publisher: { "@type": "Organization", name: "STXOSHI", logo: { "@type": "ImageObject", url: "https://stxoshi.com/stxoshi-logo.png" } },
  }), [a]);

  useSeo({
    title: a ? `${a.title} | STXOSHI News` : null,
    description: a ? (a.summary || `${a.title} — ${a.category_label} news on STXOSHI.`).slice(0, 160) : "",
    path: `/news/${slug}`, image: a?.image, jsonLd,
  });

  if (error) return <Shell><p className="mt-10 text-muted-foreground" data-testid="news-article-not-found">Story not found.</p></Shell>;
  if (!a) return <Shell><Loader2 className="mt-10 h-6 w-6 animate-spin text-primary" data-testid="news-article-loading" /></Shell>;

  return (
    <Shell>
      <article className="mt-8 rounded-2xl border border-white/10 bg-white/[0.02] p-6 sm:p-10" data-testid="news-article">
        <div className="flex flex-wrap items-center gap-2 text-xs font-mono uppercase tracking-widest text-muted-foreground">
          <Link to={`/news/category/${a.category}`} className="text-primary hover:underline" data-testid="news-article-category">{a.category_label}</Link>
          <span>•</span><span data-testid="news-article-source">{a.source}</span>
          <span>•</span><span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{timeAgo(a.published_at)}</span>
        </div>
        <h1 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-black leading-tight" data-testid="news-article-title">{a.title}</h1>
        {a.image && <img src={a.image} alt={a.title} className="mt-6 w-full rounded-xl object-cover max-h-[420px]" />}
        <p className="mt-6 text-lg text-muted-foreground leading-relaxed" data-testid="news-article-summary">
          {a.summary || `${host(a.url) || a.source} is reporting on the story above. It caught our attention because it relates to ${a.category_label.toLowerCase()} — a core theme for the STXOSHI agentic economy.`}
        </p>
        <a href={a.url} target="_blank" rel="noopener noreferrer nofollow" data-testid="news-article-read-full"
          className="mt-8 flex items-center gap-3 rounded-xl bg-primary px-6 py-4 font-semibold text-black hover:brightness-110 transition-[filter]">
          <ExternalLink className="h-5 w-5 shrink-0" /> Read the full story at {host(a.url) || a.source}
        </a>
        <TakeBox take={a.stxoshi_take} />
      </article>
      {a.ai_take && (
        <section className="mt-8 rounded-2xl border border-primary/30 bg-gradient-to-b from-primary/[0.07] to-transparent p-6 sm:p-10" data-testid="news-stxoshi-take">
          <p className="text-xs font-mono uppercase tracking-normal text-primary">STXOSHI's Take</p>
          <p className="mt-4 text-lg leading-relaxed text-white/90" data-testid="news-stxoshi-take-text">{a.ai_take}</p>
          <NewsShareBar article={a} />
        </section>
      )}
      {!a.ai_take && <NewsShareBar article={a} />}
      {data.related.length > 0 && (
        <section className="mt-12" data-testid="news-related">
          <h2 className="font-heading text-lg tracking-wider">More {a.category_label.replace(/ news$/i, "")} news</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {data.related.map((r, i) => <NewsCard key={r.slug} item={r} index={i} />)}
          </div>
        </section>
      )}
    </Shell>
  );
}

const Shell = ({ children }) => (
  <div className="min-h-screen bg-black text-white">
    <Header />
    <div className="max-w-4xl mx-auto px-6 py-10">
      <Link to="/news" data-testid="news-article-back" className="inline-flex items-center gap-2 text-sm font-mono text-muted-foreground hover:text-primary">
        <ArrowLeft className="h-4 w-4" /> All news
      </Link>
      {children}
    </div>
  </div>
);

const TakeBox = ({ take }) => (
  <div className="mt-8 rounded-xl border border-primary/25 bg-primary/[0.04] p-6" data-testid="news-article-take">
    <p className="text-xs font-mono uppercase tracking-normal text-primary">The STXOSHI angle</p>
    <p className="mt-3 text-white/90">{take}</p>
    <div className="mt-5 grid gap-3 sm:grid-cols-3 text-sm">
      <Link to="/bot/create" data-testid="news-cta-bot" className="flex items-center gap-2 text-muted-foreground hover:text-primary"><Bot className="h-4 w-4 text-primary" />Launch an AI trading bot</Link>
      <Link to="/perps" data-testid="news-cta-perps" className="flex items-center gap-2 text-muted-foreground hover:text-primary"><LineChart className="h-4 w-4 text-primary" />Trade STX perps</Link>
      <Link to="/dashboard" data-testid="news-cta-dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-primary"><Coins className="h-4 w-4 text-primary" />Earn sBTC yield</Link>
    </div>
    <div className="mt-3 grid gap-3 sm:grid-cols-3 text-sm" data-testid="news-cta-row-2">
      <SwapSTXOSHIButton trigger={
        <button type="button" data-testid="news-cta-swap" className="flex items-center gap-2 text-left text-muted-foreground hover:text-primary"><ArrowDownUp className="h-4 w-4 text-primary" />Swap STXOSHI</button>
      } />
      <Link to="/flip-market" data-testid="news-cta-flip" className="flex items-center gap-2 text-muted-foreground hover:text-primary"><Repeat className="h-4 w-4 text-primary" />Flip STXOSHI</Link>
      <Link to="/dashboard" data-testid="news-cta-explore" className="flex items-center gap-2 text-muted-foreground hover:text-primary"><Compass className="h-4 w-4 text-primary" />Explore Dashboard</Link>
    </div>
  </div>
);
